import type { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { asyncScheduler, Observable, scheduled, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, share } from 'rxjs/operators';
import {
	CONFIGURATION_SERVICE,
	IAuthTokenService,
	IConfigurationService,
	IServerApiBuilderService,
	MobizServerApi,
	MobizSession,
	OAuthTokenRefreshError,
} from '../../core';
import { Inject, Injectable } from '../../injection';
import { VOID } from '../../rxjs-utils';
import { SerializableParams } from '../../utils';
import { AXIOS_STATIC_HTTP_CLIENT } from '../other/axios-static-http-client.factory';

import { AuthGrantType } from '../../core/services/auth-token-grant';
import { ConnectionHelperService } from './connection-helper.service';

/**
 * @internal
 */
@Injectable()
export class TokenRefreshService {
	private refreshTokenObservable: Observable<void> | undefined;

	constructor(
		@Inject(IAuthTokenService) private readonly authTokenService: IAuthTokenService,
		@Inject(AXIOS_STATIC_HTTP_CLIENT) private readonly axiosStatic: AxiosInstance,
		@Inject(CONFIGURATION_SERVICE) public readonly configurationService: IConfigurationService,
		@Inject(IServerApiBuilderService) private readonly serverApiBuilderService: IServerApiBuilderService,
		private readonly connectionHelperService: ConnectionHelperService
	) {}

	/**
	 * Refresh the authentication token.
	 */
	public refreshToken(): Observable<void> {
		if (!this.refreshTokenObservable) {
			// NOTE: The observables are built asynchronously to avoid issues with subsequent requests in the JS event loop.
			// @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/EventLoop
			this.refreshTokenObservable = scheduled(VOID, asyncScheduler).pipe(
				mergeMap((): Observable<void> => {
					return this.getRefreshTokenObservable(this.authTokenService.session.grantType);
				}),
				finalize((): void => {
					delete this.refreshTokenObservable;
				}),
				share()
			);
		}
		return this.refreshTokenObservable;
	}

	private getRefreshTokenObservable(grantType: AuthGrantType): Observable<void> {
		const requestUrl = this.serverApiBuilderService.getUrl(MobizServerApi.RefreshToken);
		const requestPayload: SerializableParams = this.authTokenService.getTokenRefreshRequestBody(grantType);
		const extraHeaders = this.connectionHelperService.prepareHeaders(
			AuthGrantType.RefreshToken,
			undefined,
			requestPayload
		);
		return scheduled(
			this.axiosStatic({
				method: 'POST',
				url: requestUrl,
				data: requestPayload.toString(),
				headers: this.connectionHelperService.convertHeadersToRecord(extraHeaders),
				responseType: 'json',
				withCredentials: true,
			}),
			asyncScheduler
		).pipe(
			catchError((error: AxiosError): Observable<never> => {
				return this.throwOAuthTokenRefreshError(error);
			}),
			map((axiosResponse: AxiosResponse<unknown>): void => {
				const mobizSession = new MobizSession(axiosResponse.data);
				// The response doesn't include the Grant Type, which is required.
				mobizSession.grantType = grantType;
				this.authTokenService.session = mobizSession;
			})
		);
	}

	private throwOAuthTokenRefreshError(error: unknown): Observable<never> {
		const message: string = 'An error occurred during token refresh.';
		const refreshError: OAuthTokenRefreshError = new OAuthTokenRefreshError(message, error);
		return throwError(refreshError);
	}
}
